import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtDate, formatDuration } from "../lib/formatters";
import { Camera, ExternalLink, Save, Pencil, Video, Filter } from "lucide-react";
import DestCityFilter from "../components/DestCityFilter";

const isValidUrl = (s) => {
  if (!s) return false;
  try { new URL(s.startsWith("http") ? s : `https://${s}`); return true; } catch { return false; }
};
const normalizeUrl = (s) => (s.startsWith("http://") || s.startsWith("https://") ? s : `https://${s}`);

function LinkCell({ value, onSave, testidPrefix, rowId, placeholder }) {
  const [editing, setEditing] = useState(!value);
  const [draft, setDraft] = useState(value || "");

  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed && !isValidUrl(trimmed)) {
      toast.error("Please enter a valid URL");
      return;
    }
    await onSave(trimmed ? normalizeUrl(trimmed) : "");
    setEditing(false);
  };

  if (!editing && value) {
    return (
      <div className="flex items-center gap-2">
        <a data-testid={`${testidPrefix}-link-${rowId}`} href={value} target="_blank" rel="noreferrer"
          className="text-brand hover:underline text-sm font-medium flex items-center gap-1 min-w-0">
          <ExternalLink size={12} className="shrink-0" />
          <span className="truncate max-w-[220px]">{value}</span>
        </a>
        <button data-testid={`${testidPrefix}-edit-${rowId}`} onClick={() => { setDraft(value); setEditing(true); }}
          className="text-[color:var(--aii-muted)] hover:text-brand" title="Edit">
          <Pencil size={12} />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <input data-testid={`${testidPrefix}-input-${rowId}`} value={draft} onChange={e => setDraft(e.target.value)}
        placeholder={placeholder}
        className="flex-1 px-2 py-1 border border-[color:var(--aii-border)] rounded bg-white text-sm min-w-0"
        onKeyDown={e => { if (e.key === "Enter") save(); }} />
      <button data-testid={`${testidPrefix}-save-${rowId}`} onClick={save}
        className="text-xs px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark flex items-center gap-1 shrink-0">
        <Save size={11} /> Save
      </button>
    </div>
  );
}

export default function MediaLog() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ destination: "", city: "" });
  const [showOnlyPending, setShowOnlyPending] = useState(false);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.destination) q.set("destination", filter.destination);
    if (filter.city) q.set("city", filter.city);
    const { data } = await api.get(`/program-bookings?${q.toString()}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [filter.destination, filter.city]);

  const visibleRows = useMemo(() => {
    if (!showOnlyPending) return rows;
    return rows.filter(r => !(r.pictures_link || "").trim() || !(r.testimonials_link || "").trim());
  }, [rows, showOnlyPending]);
  const pendingCount = useMemo(
    () => rows.filter(r => !(r.pictures_link || "").trim() || !(r.testimonials_link || "").trim()).length,
    [rows],
  );

  const savePictures = async (id, link) => {
    await api.patch(`/program-bookings/${id}`, { pictures_link: link });
    toast.success(link ? "Pictures link saved" : "Pictures link cleared");
    await load();
  };
  const saveTestimonials = async (id, link) => {
    await api.patch(`/program-bookings/${id}`, { testimonials_link: link });
    toast.success(link ? "Testimonial link saved" : "Testimonial link cleared");
    await load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Social Media & Content</div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Camera className="text-brand" /> Pictures & Videos</h1>
          <p className="text-sm text-[color:var(--aii-text-2)] mt-1">
            Log the Google Drive link of pictures &amp; videos and the video testimonial (Google Drive or YouTube) for every participant. Once saved, the link becomes clickable and opens in a new window.
          </p>
        </div>
        <DestCityFilter value={filter} onChange={setFilter} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="media-pending-toggle"
          onClick={() => setShowOnlyPending(s => !s)}
          className={`text-xs px-3 py-1.5 rounded-md border font-semibold flex items-center gap-1.5 transition ${
            showOnlyPending
              ? "bg-brand text-white border-brand"
              : "bg-white text-[color:var(--aii-text-1)] border-[color:var(--aii-border)] hover:bg-[color:var(--aii-green-tint)]"
          }`}
          title="Toggle to hide participants whose Drive links are already filled"
        >
          <Filter size={11}/> {showOnlyPending ? "Showing pending only" : "Show only pending upload"}
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${showOnlyPending ? "bg-white/20" : "bg-brand/10 text-brand"}`} data-testid="media-pending-count">
            {pendingCount}
          </span>
        </button>
        <span className="text-[11px] text-[color:var(--aii-muted)]">
          {showOnlyPending
            ? `Hiding ${rows.length - visibleRows.length} participant${rows.length - visibleRows.length === 1 ? "" : "s"} with both links already saved.`
            : "Click above to hide participants whose Pictures & Video Testimonial links are already saved."}
        </span>
      </div>

      <div className="aii-card overflow-x-auto" data-testid="media-log-table">
        <table className="aii-table">
          <thead>
            <tr>
              <th>Partner</th>
              <th>Destination</th>
              <th>City</th>
              <th>Participant</th>
              <th>Gender</th>
              <th>DOB</th>
              <th>Programme</th>
              <th>Duration</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th><span className="flex items-center gap-1"><Camera size={11}/> Pictures &amp; Videos <span className="text-[10px] font-normal text-[color:var(--aii-muted)]">(Google Drive Link)</span></span></th>
              <th><span className="flex items-center gap-1"><Video size={11}/> Video Testimonial <span className="text-[10px] font-normal text-[color:var(--aii-muted)]">(Drive or YouTube)</span></span></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr><td colSpan={12} className="text-center py-10 text-[color:var(--aii-muted)]">
                {showOnlyPending ? "🎉 All participants have both Drive links saved — nothing pending." : "No program bookings yet."}
              </td></tr>
            )}
            {visibleRows.map(r => (
              <tr key={r.id} data-testid={`media-row-${r.id}`}>
                <td className="font-medium text-brand text-xs" data-testid={`media-partner-${r.id}`}>{r.partner_name || <span className="italic text-[color:var(--aii-muted)] font-normal">—</span>}</td>
                <td className="text-xs whitespace-nowrap">{r.destination || "—"}</td>
                <td className="text-xs whitespace-nowrap">{r.city || "—"}</td>
                <td className="font-medium">{r.customer || "—"}</td>
                <td className="text-xs">{r.gender || "—"}</td>
                <td className="text-xs whitespace-nowrap">{fmtDate(r.dob) || "—"}</td>
                <td>{r.programme || "India Experience Program"}</td>
                <td className="text-xs whitespace-nowrap">{formatDuration(r.duration) || "—"}</td>
                <td className="whitespace-nowrap">{fmtDate(r.start_date) || "—"}</td>
                <td className="whitespace-nowrap">{fmtDate(r.end_date) || "—"}</td>
                <td>
                  <LinkCell value={r.pictures_link || ""} onSave={(v) => savePictures(r.id, v)}
                    testidPrefix="pics" rowId={r.id} placeholder="https://drive.google.com/…" />
                </td>
                <td>
                  <LinkCell value={r.testimonials_link || ""} onSave={(v) => saveTestimonials(r.id, v)}
                    testidPrefix="test" rowId={r.id} placeholder="https://drive.google.com/… or https://youtu.be/…" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
